import React, { useEffect, useRef, useState } from 'react';
import { Clapperboard, Download, FileUp, Image as ImageIcon, Play, Plus, RefreshCw, Send, Trash2, Trophy, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';
import FinalistCampaigns from './FinalistCampaigns';

const API_BASE = api.defaults.baseURL;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

function VenuePicker({ finalist, onLinked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  function search(text) {
    setQ(text);
    clearTimeout(timer.current);
    if (text.trim().length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/awards/attraction-search?q=${encodeURIComponent(text.trim())}`);
        setResults(r.data.items || []); setOpen(true);
      } catch { /* noop */ }
    }, 250);
  }

  async function link(slug, type) {
    setBusy(true);
    const body = type === 'event' ? { event_slug: slug } : { attraction_slug: slug };
    try { await api.patch(`/admin/awards/finalists/${finalist.id}`, body); setQ(''); setOpen(false); onLinked(); }
    catch (ex) { alert(ex.response?.data?.detail || 'Link failed'); }
    finally { setBusy(false); }
  }

  async function createListing() {
    if (!window.confirm(`Create a new draft listing called "${finalist.name}" and link this finalist to it? You can finish the details under Attractions later.`)) return;
    setBusy(true);
    try { await api.post(`/admin/awards/finalists/${finalist.id}/create-listing`); onLinked(); }
    catch (ex) { alert(ex.response?.data?.detail || 'Could not create the listing'); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-1.5">
      {finalist.suggestions?.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`finalist-suggestions-${finalist.id}`}>
          <span className="text-[10px] uppercase tracking-wide text-charcoal/40 font-bold self-center">Is it…</span>
          {finalist.suggestions.map((s) => (
            <button key={s.slug} onClick={() => link(s.slug, 'venue')} disabled={busy} data-testid={`finalist-suggestion-${finalist.id}-${s.slug}`}
              className="px-2 py-0.5 rounded-full border border-forest/40 text-forest text-[11px] hover:bg-forest/10 disabled:opacity-50">
              {s.name}{s.town ? ` (${s.town})` : ''}?
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input value={q} onChange={(e) => search(e.target.value)} onFocus={() => q.trim().length >= 2 && setOpen(true)}
          placeholder="search venues to link…" data-testid={`finalist-venue-search-${finalist.id}`}
          className="border rounded-lg px-2 py-1 text-xs w-52"/>
        {open && results.length > 0 && (
          <div className="absolute z-20 mt-1 w-72 bg-white border border-charcoal/15 rounded-xl shadow-lg overflow-hidden" data-testid={`finalist-venue-results-${finalist.id}`}>
            {results.map((r) => (
              <button key={`${r.type}-${r.slug}`} onClick={() => link(r.slug, r.type)} disabled={busy}
                className="w-full text-left px-3 py-2 text-xs hover:bg-stone flex items-center justify-between gap-2">
                <span className="truncate">{r.name}{r.town ? <span className="text-charcoal/40"> · {r.town}</span> : null}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${r.type === 'event' ? 'bg-heritage/10 text-heritage' : 'bg-charcoal/10 text-charcoal/60'}`}>{r.type === 'event' ? 'event' : 'venue'}</span>
                  <span className={`text-[9px] uppercase font-bold ${r.published ? 'text-forest' : 'text-yellow-700'}`}>{r.published ? 'live' : 'draft'}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button onClick={createListing} disabled={busy} data-testid={`finalist-create-listing-${finalist.id}`}
        className="px-2 py-1 rounded-lg border border-heritage/40 text-heritage text-[11px] hover:bg-heritage/5 disabled:opacity-50">
        + Create new listing for this finalist
      </button>
    </div>
  );
}

function FinalistsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [emails, setEmails] = useState({});
  const [edits, setEdits] = useState({}); // {id: {name, category}}
  const [preview, setPreview] = useState(null); // {file, headers, sample_rows, total_rows, mapping}
  const [fillReport, setFillReport] = useState(null);
  const [filling, setFilling] = useState(false);
  const [missing, setMissing] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', category: '', email: '' });

  async function addFinalist() {
    setBusy(true); setErr('');
    try {
      const r = await api.post('/admin/awards/finalists', {
        name: addForm.name.trim(), category: addForm.category.trim(), email: addForm.email.trim() || null });
      setAddForm({ name: '', category: '', email: '' }); setShowAdd(false);
      setResult(null);
      load();
      if (!r.data.finalist.attraction_slug) setErr('');
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not add the finalist');
    } finally { setBusy(false); }
  }
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  async function fillMissingImages() {
    if (!window.confirm('Search each finalist\'s own website for a photo and add it to any linked listing that has no image yet?')) return;
    setFilling(true); setFillReport(null);
    try {
      await api.post('/admin/awards/finalists/fill-missing-images');
      pollRef.current = setInterval(async () => {
        try {
          const r = (await api.get('/admin/awards/finalists/fill-missing-images/latest')).data.report;
          setFillReport(r);
          if (r?.done) { clearInterval(pollRef.current); setFilling(false); load(); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (ex) { setErr(ex.response?.data?.detail || 'Could not start the photo fill'); setFilling(false); }
  }
  useEffect(() => () => clearInterval(pollRef.current), []);

  async function saveField(f, field) {
    const val = (edits[f.id]?.[field] ?? f[field] ?? '').trim();
    if (!val || val === f[field]) return;
    try { await api.patch(`/admin/awards/finalists/${f.id}`, { [field]: val }); load(); }
    catch (ex) { alert(ex.response?.data?.detail?.[0]?.msg || ex.response?.data?.detail || 'Save failed'); }
  }

  function setEdit(id, field, value) {
    setEdits((m) => ({ ...m, [id]: { ...m[id], [field]: value } }));
  }

  async function saveEmail(f) {
    const email = (emails[f.id] !== undefined ? emails[f.id] : (f.email || '')).trim();
    if (!email) return;
    try { await api.patch(`/admin/awards/finalists/${f.id}`, { email }); load(); }
    catch (ex) { alert(ex.response?.data?.detail?.[0]?.msg || ex.response?.data?.detail || 'Invalid email'); }
  }

  async function unlink(f) {
    try { await api.patch(`/admin/awards/finalists/${f.id}`, { attraction_slug: '' }); load(); }
    catch (ex) { alert(ex.response?.data?.detail || 'Unlink failed'); }
  }

  async function load() {
    try { setItems((await api.get('/admin/awards/finalists')).data.items || []); }
    catch { setErr('Failed to load finalists'); }
    try { setMissing((await api.get('/admin/awards/finalists/missing-images')).data.items || []); }
    catch { /* panel simply hides */ }
  }
  useEffect(() => { load(); }, []);

  async function importFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr(''); setResult(null);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/admin/awards/finalists/import-preview', fd);
      const s = r.data.suggested || {};
      setPreview({
        file: f, headers: r.data.headers, sample_rows: r.data.sample_rows, total_rows: r.data.total_rows,
        mapping: { name: s.name ?? '', category: s.category ?? '', email: s.email ?? '', website: s.website ?? '' },
      });
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not read the file');
    } finally { setBusy(false); e.target.value = ''; }
  }

  async function confirmImport() {
    const m = preview.mapping;
    if (m.name === '' || m.category === '') { alert('Pick which column holds the venue name and which holds the category.'); return; }
    if (items.length && !window.confirm('This replaces the current finalist list (badges move to the new list and any unpublished stub listings from earlier imports are removed). Continue?')) return;
    setBusy(true); setErr('');
    const fd = new FormData();
    fd.append('file', preview.file);
    fd.append('name_col', m.name);
    fd.append('category_col', m.category);
    if (m.email !== '') fd.append('email_col', m.email);
    if (m.website !== '') fd.append('website_col', m.website);
    try {
      const r = await api.post('/admin/awards/finalists/import', fd);
      setResult(r.data);
      setPreview(null);
      load();
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Import failed');
    } finally { setBusy(false); }
  }

  const unlinkedCount = items.filter((i) => !i.attraction_slug).length;

  return (
    <div className="space-y-4">
      <HelpCallout title="How the finalist import works">
        Upload your spreadsheet and you'll see its columns with a live preview — <strong>you choose which column is the venue name, category and email</strong>, then confirm. Everything is imported exactly as it appears in the sheet and used verbatim for the announcement mail merge.
        A finalist is only linked to a venue listing automatically when the name matches <strong>exactly</strong> — nothing is renamed and no listings are created without you asking.
        For the rest, use the search box to pick the right venue, tap a suggestion, or create a new draft listing with one click.
        <strong>Names and categories are editable</strong> — click into them, type, and they save when you press Enter or click away. The public /awards page updates instantly, and all "Taste of…" categories display together as one group there.
      </HelpCallout>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={importFile} className="hidden" data-testid="finalists-file-input"/>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="finalists-import-btn"
          className="px-4 py-2 rounded-xl bg-heritage text-cream font-semibold flex items-center gap-2 disabled:opacity-50">
          <FileUp size={15}/> {busy ? 'Importing…' : 'Import finalists spreadsheet'}
        </button>
        <button onClick={load} data-testid="finalists-refresh-btn" className="px-4 py-2 rounded-xl border flex items-center gap-2"><RefreshCw size={14}/> Refresh</button>
        <button onClick={() => window.open(`${API_BASE}/admin/awards/finalist-images/download-all?token=${encodeURIComponent(localStorage.getItem('gl_admin_token') || '')}`, '_blank')}
          data-testid="finalists-programme-pack-btn"
          className="px-4 py-2 rounded-xl bg-heritage text-cream font-semibold flex items-center gap-2">
          <Download size={14}/> Programme pack (images + text)
        </button>
        <button onClick={fillMissingImages} disabled={filling} data-testid="finalists-fill-images-btn"
          className="px-4 py-2 rounded-xl border border-heritage/40 text-heritage font-semibold flex items-center gap-2 disabled:opacity-50">
          <ImageIcon size={14}/> {filling ? 'Finding photos…' : 'Fill missing photos'}
        </button>
        <button onClick={() => setShowAdd((s) => !s)} data-testid="finalists-add-btn"
          className="px-4 py-2 rounded-xl border border-forest/40 text-forest font-semibold flex items-center gap-2">
          <Plus size={14}/> Add finalist
        </button>
        {items.length > 0 && <span className="text-sm text-charcoal/60">{items.length} finalists{unlinkedCount ? ` · ${unlinkedCount} not linked to a listing yet` : ' · all linked'}</span>}
      </div>
      {showAdd && (
        <div data-testid="finalists-add-form" className="p-4 rounded-2xl bg-white border border-charcoal/10 flex flex-wrap items-end gap-3">
          <label className="text-sm font-medium">Finalist / business name
            <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-finalist-name-input"
              placeholder="e.g. Chester Thorne - Imago Venues" className="mt-1 block w-72 border rounded-xl px-3 py-2 font-normal"/>
          </label>
          <label className="text-sm font-medium">Category
            <input value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} data-testid="add-finalist-category-input"
              list="finalist-cats" placeholder="e.g. Rising Star" className="mt-1 block w-56 border rounded-xl px-3 py-2 font-normal"/>
            <datalist id="finalist-cats">{[...new Set(items.map((i) => i.category))].map((c) => <option key={c} value={c}/>)}</datalist>
          </label>
          <label className="text-sm font-medium">Email <span className="text-charcoal/40">(for the announcement)</span>
            <input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="add-finalist-email-input"
              type="email" className="mt-1 block w-64 border rounded-xl px-3 py-2 font-normal"/>
          </label>
          <button onClick={addFinalist} disabled={busy || !addForm.name.trim() || !addForm.category.trim()} data-testid="add-finalist-save-btn"
            className="px-5 py-2 rounded-xl bg-forest text-cream font-semibold disabled:opacity-50">Add to finalists</button>
          <span className="text-xs text-charcoal/50 basis-full">If the name exactly matches an existing listing it links automatically — otherwise link it or create a draft listing from the table below.</span>
        </div>
      )}
      {result && (
        <div data-testid="finalists-import-result" className="p-3 rounded-xl bg-forest/10 text-forest text-sm">
          Imported {result.imported} finalists exactly as in the spreadsheet — {result.matched} linked automatically (exact name matches), {result.unmatched} to link below when you're ready.
          {result.with_email > 0 && <> {result.with_email} came with an email address for the mailing.</>}
          {result.stubs_removed > 0 && <> Cleaned up {result.stubs_removed} leftover stub listing{result.stubs_removed === 1 ? '' : 's'} from the previous import.</>}
        </div>
      )}
      {err && <div className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm" data-testid="finalists-error">{err}</div>}
      {missing.length > 0 && (
        <div data-testid="finalists-missing-images-panel" className="p-4 rounded-xl bg-mustard/15 border border-mustard/40 text-sm space-y-1.5">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle size={14} className="text-heritage"/>
            {missing.length} finalist{missing.length === 1 ? '' : 's'} counted as "missing photo" on the dashboard — their public awards tile shows the finalist logo instead of a photo:</div>
          <ul className="space-y-1">
            {missing.map((m) => (
              <li key={`${m.id}-${m.reason}`} data-testid={`missing-image-row-${m.id}`}>
                <strong>{m.finalist}</strong> <span className="text-charcoal/50">({m.category})</span> — {
                  m.reason === 'not_linked' ? 'not linked to any listing yet — link or create one below'
                  : m.reason === 'listing_missing' ? `linked to "${m.slug}" which no longer exists — re-link it below`
                  : `its listing "${m.slug}" has no photo — add one (or use Fill missing photos)`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {fillReport && (
        <div data-testid="fill-images-report" className={`p-3 rounded-xl text-sm ${fillReport.done ? 'bg-forest/10 text-forest' : 'bg-stone text-charcoal/70'}`}>
          {fillReport.done ? 'Photo search finished. ' : 'Searching venue websites for photos… '}
          {(() => {
            const rs = fillReport.results || [];
            const filled = rs.filter((r) => r.status === 'filled');
            const noSite = rs.filter((r) => r.status === 'no_website');
            const noImg = rs.filter((r) => r.status === 'no_image_found');
            return (
              <>
                {filled.length > 0 && <>Added photos for: <strong>{filled.map((r) => r.finalist).join(', ')}</strong>. </>}
                {noImg.length > 0 && <>No usable photo found on the website of: {noImg.map((r) => r.finalist).join(', ')}. </>}
                {noSite.length > 0 && <>No website on the listing for: {noSite.map((r) => r.finalist).join(', ')} — add one and re-run. </>}
                {fillReport.done && rs.length === 0 && <>Every linked finalist listing already has a photo — the gaps left are finalists not linked to a listing.</>}
              </>
            );
          })()}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4" data-testid="import-mapping-modal">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-3xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-2xl mb-1">Check the columns before importing</h3>
            <p className="text-sm text-charcoal/60 mb-5">{preview.total_rows} rows found. Tell us which column is which — the preview below shows exactly what will be imported.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              {[
                ['name', 'Venue / finalist name', true],
                ['category', 'Award category', true],
                ['email', 'Email for the mailing', false],
                ['website', 'Website', false],
              ].map(([key, label, required]) => (
                <label key={key} className="block text-sm font-medium">
                  {label}{required ? '' : ' (optional)'}
                  <select value={preview.mapping[key]} data-testid={`import-map-${key}`}
                    onChange={(e) => setPreview((p) => ({ ...p, mapping: { ...p.mapping, [key]: e.target.value === '' ? '' : Number(e.target.value) } }))}
                    className="mt-1 w-full border rounded-xl px-3 py-2 bg-white text-sm">
                    <option value="">{required ? '— choose a column —' : '— none —'}</option>
                    {preview.headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <div className="border border-charcoal/10 rounded-xl overflow-hidden mb-5">
              <div className="bg-stone/60 px-3 py-2 text-[10px] uppercase tracking-wide font-bold text-charcoal/50">What will be imported (first {preview.sample_rows.length} rows)</div>
              <table className="w-full text-xs" data-testid="import-mapping-preview">
                <thead><tr className="text-left text-charcoal/50 border-b border-charcoal/10">
                  <th className="px-3 py-1.5">Venue name</th><th className="px-3 py-1.5">Category</th><th className="px-3 py-1.5">Email</th>
                </tr></thead>
                <tbody>
                  {preview.sample_rows.map((r, ri) => (
                    <tr key={ri} className="border-b border-charcoal/5 last:border-0">
                      <td className="px-3 py-1.5 font-medium">{preview.mapping.name === '' ? <span className="text-heritage">choose column ↑</span> : (r[preview.mapping.name] || '—')}</td>
                      <td className="px-3 py-1.5">{preview.mapping.category === '' ? <span className="text-heritage">choose column ↑</span> : (r[preview.mapping.category] || '—')}</td>
                      <td className="px-3 py-1.5 text-charcoal/60">{preview.mapping.email === '' ? '—' : (r[preview.mapping.email] || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setPreview(null)} data-testid="import-mapping-cancel" className="px-4 py-2 rounded-xl border font-semibold">Cancel</button>
              <button onClick={confirmImport} disabled={busy || preview.mapping.name === '' || preview.mapping.category === ''} data-testid="import-mapping-confirm"
                className="px-5 py-2 rounded-xl bg-heritage text-cream font-semibold disabled:opacity-50">
                {busy ? 'Importing…' : `Import ${preview.total_rows} finalists`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
          <thead className="bg-admin text-left text-xs uppercase tracking-wide text-charcoal/60">
            <tr><th className="p-3">Finalist (as in spreadsheet)</th><th className="p-3">Category</th><th className="p-3">Email for mailing</th><th className="p-3">Venue listing</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.id} data-testid={`finalist-row-${f.id}`} className="border-t border-charcoal/5 text-sm align-top">
                <td className="p-3">
                  <input value={edits[f.id]?.name ?? f.name}
                    onChange={(e) => setEdit(f.id, 'name', e.target.value)}
                    onBlur={() => saveField(f, 'name')}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    data-testid={`finalist-name-input-${f.id}`}
                    className="border border-transparent hover:border-charcoal/20 focus:border-heritage rounded-lg px-2 py-1 font-medium w-56 bg-transparent"/>
                  {f.announcement_sent_at && <div className="text-[10px] text-forest font-semibold mt-0.5" title={f.announcement_sent_at}>✓ announcement emailed</div>}
                </td>
                <td className="p-3">
                  <input value={edits[f.id]?.category ?? f.category}
                    onChange={(e) => setEdit(f.id, 'category', e.target.value)}
                    onBlur={() => saveField(f, 'category')}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    data-testid={`finalist-category-input-${f.id}`}
                    className="border border-transparent hover:border-charcoal/20 focus:border-heritage rounded-lg px-2 py-1 text-charcoal/70 w-56 bg-transparent"/>
                </td>
                <td className="p-3">
                  <div className="flex gap-1.5 items-center">
                    <input value={emails[f.id] !== undefined ? emails[f.id] : (f.email || '')}
                      onChange={(e) => setEmails((m) => ({ ...m, [f.id]: e.target.value }))}
                      placeholder="contact email…" data-testid={`finalist-email-input-${f.id}`}
                      className="border rounded-lg px-2 py-1 text-xs w-52"/>
                    <button onClick={() => saveEmail(f)} data-testid={`finalist-email-save-${f.id}`} className="px-2 py-1 rounded-lg border text-xs">Save</button>
                  </div>
                </td>
                <td className="p-3">
                  {(f.attraction_slug || f.event_slug) ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {f.event_slug ? (
                          <a href={`/events/${f.event_slug}`} target="_blank" rel="noreferrer" className="text-heritage hover:underline text-xs">{f.event_slug}</a>
                        ) : (
                          <a href={`/explore/${f.attraction_slug}`} target="_blank" rel="noreferrer" className="text-heritage hover:underline text-xs">{f.attraction_slug}</a>
                        )}
                        {f.event_slug && <span className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-heritage/10 text-heritage">Event</span>}
                        {f.published ? (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-forest/10 text-forest"><CheckCircle2 size={9}/> Live</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800"><AlertTriangle size={9}/> Draft</span>
                        )}
                      </div>
                      <button onClick={() => unlink(f)} data-testid={`finalist-unlink-${f.id}`}
                        className="text-[11px] text-charcoal/50 hover:text-heritage underline">Unlink / change venue</button>
                    </div>
                  ) : (
                    <VenuePicker finalist={f} onLinked={load}/>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button onClick={async () => { if (window.confirm(`Remove ${f.name} from the finalists list? Their badge is removed too.`)) { await api.delete(`/admin/awards/finalists/${f.id}`); load(); } }}
                    data-testid={`finalist-delete-${f.id}`} className="p-1.5 rounded-lg hover:bg-stone text-heritage"><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-charcoal/50">No finalists imported yet — upload the spreadsheet when it's ready.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FONT_OPTIONS = [
  { key: 'georgia', label: 'Georgia (serif)', stack: "Georgia, 'Times New Roman', serif" },
  { key: 'times', label: 'Times New Roman', stack: "'Times New Roman', Times, serif" },
  { key: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { key: 'helvetica', label: 'Helvetica', stack: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { key: 'verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { key: 'trebuchet', label: 'Trebuchet MS', stack: "'Trebuchet MS', Tahoma, sans-serif" },
];

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function bodyToHtml(body) {
  return body.split(/\n\n+/).filter((p) => p.trim()).map((p) => {
    let t = escapeHtml(p);
    t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, "<a href='$2' style='color:#C24F41;font-weight:600'>$1</a>");
    t = t.replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>');
    t = t.replace(/==([\s\S]+?)==/g, "<span style='background:#FFF3C4;padding:0 2px'>$1</span>");
    return `<p style="margin:0 0 14px">${t.replace(/\n/g, '<br/>')}</p>`;
  }).join('');
}

const SAMPLE_FILL = {
  '{business_name}': 'The Grange Courtyard',
  '{category}': 'B&B and Guest House of the Year',
  '{upload_link}': 'https://goleicestershire.com/awards/finalist-video-upload',
  '{tickets_link}': 'https://goleicestershire.com/awards/tickets',
  '{partner_signup_link}': 'https://goleicestershire.com/trade',
  '{video_deadline}': 'Friday 17 July 2026',
};

function fillSample(text) {
  return Object.entries(SAMPLE_FILL).reduce((t, [k, v]) => t.split(k).join(v), text);
}

const MERGE_FIELDS = [
  ['{business_name}', 'Business name'],
  ['{category}', 'Category'],
  ['{video_deadline}', 'Video deadline'],
  ['{upload_link}', 'Video upload link'],
  ['{tickets_link}', 'Tickets link'],
  ['{partner_signup_link}', 'Partner sign-up link'],
];

function AnnouncementPanel() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [font, setFont] = useState('georgia');
  const [bcc, setBcc] = useState('');
  const [atts, setAtts] = useState([]);
  const [meta, setMeta] = useState({});
  const [testEmail, setTestEmail] = useState('');
  const [spotCount, setSpotCount] = useState(3);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [status, setStatus] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const attRef = useRef(null);
  const bodyRef = useRef(null);

  async function load() {
    const r = await api.get('/admin/awards/announcement');
    setSubject(r.data.subject); setBody(r.data.body); setAtts(r.data.attachments || []);
    setFont(r.data.font || 'georgia'); setBcc(r.data.bcc ?? '');
    setMeta(r.data);
  }
  useEffect(() => { load(); loadStatus(); }, []);

  function wrapSelection(marker) {
    const el = bodyRef.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    if (s === e) { setMsg({ ok: false, text: 'Select the text you want to format first, then press the button.' }); return; }
    setBody(body.slice(0, s) + marker + body.slice(s, e) + marker + body.slice(e));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + marker.length, e + marker.length); });
  }

  function insertField(token) {
    const el = bodyRef.current;
    const s = el?.selectionStart ?? body.length;
    const e = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, s) + token + body.slice(e));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = s + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function insertLink() {
    const el = bodyRef.current;
    const s = el?.selectionStart ?? body.length;
    const e = el?.selectionEnd ?? body.length;
    const selected = body.slice(s, e);
    const url = window.prompt('Paste the web address — a full link (https://…) or a page on the site (e.g. /awards/tickets):', '/');
    if (!url || !url.trim()) return;
    const text = selected || window.prompt('Text to show for the link:', 'click here') || 'click here';
    const token = `[${text}](${url.trim()})`;
    setBody(body.slice(0, s) + token + body.slice(e));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(s + token.length, s + token.length);
    });
  }

  async function saveTemplate() {
    await api.put('/admin/awards/announcement', { subject, body, font, bcc });
  }
  async function save() {
    setBusy('save'); setMsg(null);
    try { await saveTemplate(); setMsg({ ok: true, text: 'Template saved.' }); }
    catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Save failed' }); }
    finally { setBusy(''); }
  }
  async function upload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy('att'); setMsg(null);
    const fd = new FormData(); fd.append('file', f);
    try { await api.post('/admin/awards/announcement/attachments', fd); load(); }
    catch (ex) { setMsg({ ok: false, text: ex.response?.data?.detail || 'Upload failed' }); }
    finally { setBusy(''); e.target.value = ''; }
  }
  async function sendTest() {
    if (!testEmail.trim()) return;
    setBusy('test'); setMsg(null);
    try {
      await saveTemplate();
      const r = await api.post('/admin/awards/announcement/test', { email: testEmail.trim() });
      setMsg(r.data.ok ? { ok: true, text: `Test email sent to ${testEmail.trim()}.` } : { ok: false, text: 'Test send failed — check the Resend configuration.' });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Test send failed' }); }
    finally { setBusy(''); }
  }
  async function spotCheck() {
    if (!testEmail.trim()) { setMsg({ ok: false, text: 'Enter your email address first (the box on the left).' }); return; }
    setBusy('spot'); setMsg(null);
    try {
      await saveTemplate();
      const r = await api.post('/admin/awards/announcement/spot-check', { email: testEmail.trim(), count: spotCount });
      const names = (r.data.sent || []).map((s) => s.business).join(', ');
      const failed = (r.data.failed || []).map((s) => s.business).join(', ');
      setMsg({ ok: r.data.ok, text: `Spot check: ${r.data.sent.length} random finalist email${r.data.sent.length === 1 ? '' : 's'} sent to ${testEmail.trim()} (${names}).${failed ? ` FAILED: ${failed}.` : ''} Nothing was sent to the finalists.` });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Spot check failed' }); }
    finally { setBusy(''); }
  }
  async function sendAll() {
    if (!window.confirm(`Send the announcement (with ${atts.length} attachment${atts.length === 1 ? '' : 's'})? Businesses that have ALREADY received it are skipped automatically — this will only email the remaining contacts.${bcc ? ` A blind copy of every email goes to ${bcc}.` : ''}`)) return;
    setBusy('send'); setMsg(null);
    try {
      await saveTemplate();
      const r = await api.post('/admin/awards/announcement/send');
      const skipped = r.data.skipped_no_email || [];
      const failed = r.data.failed || [];
      setMsg({ ok: failed.length === 0, text: `Sent ${r.data.sent} email${r.data.sent === 1 ? '' : 's'}.${r.data.already_sent_skipped ? ` ${r.data.already_sent_skipped} already-sent business${r.data.already_sent_skipped === 1 ? ' was' : 'es were'} skipped (no duplicates).` : ''}${skipped.length ? ` Skipped (no email): ${skipped.join(', ')}.` : ''}${failed.length ? ` FAILED: ${failed.join(', ')} — press Send again to retry just these.` : ''}` });
      load(); loadStatus();
    } catch (e) { setMsg({ ok: false, text: (e.response?.data?.detail || 'Send failed') + ' — don\'t worry: nobody gets a duplicate. Check "Who has received it?" below, then press Send again to continue with the remaining contacts.' }); loadStatus(); }
    finally { setBusy(''); }
  }
  async function loadStatus() {
    try { setStatus((await api.get('/admin/awards/announcement/status')).data); } catch { /* section hides */ }
  }

  const fontStack = (FONT_OPTIONS.find((f) => f.key === font) || FONT_OPTIONS[0]).stack;

  return (
    <div className="space-y-4">
      <HelpCallout title="Finalist congratulations email">
        Fully editable — the placeholders <code>{'{business_name}'}</code>, <code>{'{category}'}</code>, <code>{'{upload_link}'}</code>, <code>{'{tickets_link}'}</code>, <code>{'{partner_signup_link}'}</code> and <code>{'{video_deadline}'}</code> are filled in automatically per finalist.
        Select text and press <strong>B</strong> to make it bold or <strong>Highlight</strong> to give it a yellow highlight — the preview shows exactly what recipients see.
        A blind copy (BCC) of every finalist email goes to the address below, and you can send yourself a random spot-check batch before pressing the big red button.
      </HelpCallout>
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 space-y-4">
        <label className="block text-sm font-medium">Subject
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="announce-subject-input"
            className="mt-1.5 w-full border rounded-xl px-3 py-2"/>
        </label>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="text-sm font-medium">Email body</span>
            <div className="flex items-center gap-1.5 ml-auto">
              <button type="button" onClick={() => wrapSelection('**')} data-testid="announce-bold-btn" title="Bold the selected text"
                className="px-3 py-1.5 rounded-lg border font-bold text-sm hover:bg-stone">B</button>
              <button type="button" onClick={() => wrapSelection('==')} data-testid="announce-highlight-btn" title="Highlight the selected text"
                className="px-3 py-1.5 rounded-lg border text-sm hover:bg-stone"><span style={{ background: '#FFF3C4', padding: '0 3px' }}>Highlight</span></button>
              <button type="button" onClick={insertLink} data-testid="announce-link-btn" title="Turn the selected text into a link (or insert a new one) — works with site pages like /awards/tickets"
                className="px-3 py-1.5 rounded-lg border text-sm hover:bg-stone text-heritage font-semibold underline">Link</button>
              <select value={font} onChange={(e) => setFont(e.target.value)} data-testid="announce-font-select"
                className="border rounded-lg px-2.5 py-1.5 text-sm bg-white" title="Email font">
                {FONT_OPTIONS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <button type="button" onClick={() => setShowPreview((v) => !v)} data-testid="announce-preview-toggle"
                className="px-3 py-1.5 rounded-lg border text-sm hover:bg-stone">{showPreview ? 'Hide preview' : 'Show preview'}</button>
            </div>
          </div>
          <div className={`grid gap-4 ${showPreview ? 'lg:grid-cols-2' : ''}`}>
            <div>
              <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="merge-fields-bar">
                <span className="text-[10px] uppercase tracking-wide font-bold text-charcoal/40">Insert merge field at cursor:</span>
                {MERGE_FIELDS.map(([token, label]) => (
                  <button key={token} type="button" onClick={() => insertField(token)}
                    data-testid={`merge-field-${token.replace(/[{}]/g, '')}`}
                    title={`Inserts ${token} — replaced with the finalist's real ${label.toLowerCase()} in each email`}
                    className="px-2 py-1 rounded-full border border-forest/40 text-forest text-[11px] font-semibold hover:bg-forest/10">
                    {label}
                  </button>
                ))}
              </div>
              <textarea ref={bodyRef} rows={20} value={body} onChange={(e) => setBody(e.target.value)} data-testid="announce-body-input"
                className="w-full border rounded-xl px-3 py-2 font-mono text-xs leading-relaxed"/>
            </div>
            {showPreview && (
              <div data-testid="announce-preview" className="border border-charcoal/10 rounded-xl bg-stone/30 p-4 overflow-y-auto" style={{ maxHeight: '432px' }}>
                <div className="text-[10px] uppercase tracking-wide font-bold text-charcoal/40 mb-2">Preview — with sample finalist details filled in</div>
                <div style={{ fontFamily: fontStack, color: '#2C2A29', lineHeight: 1.55, fontSize: '14px' }}>
                  <h2 style={{ color: '#C24F41', fontFamily: fontStack }} className="text-lg mb-3">2026 Leicestershire Tourism &amp; Hospitality Awards</h2>
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: bodyToHtml(fillSample(body)) }} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm font-medium">BCC every finalist email to
            <input type="email" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="announce-bcc-input"
              placeholder="leave empty to disable" className="mt-1.5 border rounded-xl px-3 py-2 text-sm w-72 block"/>
          </label>
          <button onClick={save} disabled={busy === 'save'} data-testid="announce-save-btn"
            className="px-4 py-2 rounded-xl bg-charcoal text-cream font-semibold disabled:opacity-50">Save template</button>
        </div>
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 space-y-3">
        <div className="font-semibold text-sm">Attachments <span className="font-normal text-charcoal/50">(finalist logo · guidance sheet · printable poster — JPEG/PNG/PDF)</span></div>
        {atts.map((a) => (
          <div key={a.id} data-testid={`announce-attachment-${a.id}`} className="flex items-center gap-3 text-sm border border-charcoal/10 rounded-xl px-3 py-2">
            <span className="font-medium">{a.filename}</span>
            <span className="text-xs text-charcoal/50">{(a.size / (1024 * 1024)).toFixed(1)}MB · {a.content_type}</span>
            <button onClick={async () => { await api.delete(`/admin/awards/announcement/attachments/${a.id}`); load(); }}
              data-testid={`announce-attachment-delete-${a.id}`} className="ml-auto p-1 rounded hover:bg-stone text-heritage"><Trash2 size={13}/></button>
          </div>
        ))}
        <input ref={attRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={upload} className="hidden" data-testid="announce-attachment-input"/>
        <button onClick={() => attRef.current?.click()} disabled={busy === 'att' || atts.length >= 5} data-testid="announce-attachment-add-btn"
          className="px-4 py-2 rounded-xl border flex items-center gap-2 disabled:opacity-50"><FileUp size={14}/> {busy === 'att' ? 'Uploading…' : 'Add attachment'}</button>
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 space-y-3">
        <div className="font-semibold text-sm">Check before you send</div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com"
            data-testid="announce-test-email-input" className="border rounded-xl px-3 py-2 text-sm w-64"/>
          <button onClick={sendTest} disabled={busy === 'test' || !testEmail.trim()} data-testid="announce-test-send-btn"
            className="px-4 py-2 rounded-xl border font-semibold disabled:opacity-50">{busy === 'test' ? 'Sending…' : 'Send me a test'}</button>
          <div className="flex items-center gap-2">
            <select value={spotCount} onChange={(e) => setSpotCount(Number(e.target.value))} data-testid="announce-spot-count"
              className="border rounded-xl px-2.5 py-2 text-sm bg-white">
              {[1, 2, 3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button onClick={spotCheck} disabled={busy === 'spot' || !testEmail.trim()} data-testid="announce-spot-check-btn"
              className="px-4 py-2 rounded-xl border font-semibold disabled:opacity-50" title="Sends the exact personalised emails for randomly chosen finalists to your address — nothing goes to the finalists">
              {busy === 'spot' ? 'Sending…' : 'Spot-check random finalist emails'}
            </button>
          </div>
        </div>
        <div className="text-xs text-charcoal/50">Spot-check sends you the <strong>exact personalised email</strong> for randomly chosen finalists (subject prefixed with who it would go to). Nothing is sent to the finalists and nothing is marked as sent.</div>
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-charcoal/10">
          <button onClick={sendAll} disabled={busy === 'send' || !meta.recipients_with_email} data-testid="announce-send-all-btn"
            className="px-5 py-2 rounded-xl bg-heritage text-cream font-semibold disabled:opacity-50">
            {busy === 'send' ? 'Sending…' : `Send to all finalists (${meta.recipients_with_email || 0} of ${meta.recipients_total || 0} have emails)`}
          </button>
          {bcc && <span className="text-xs text-charcoal/50">Blind copy of every email → <strong>{bcc}</strong></span>}
        </div>
        {status && (
          <div className="text-xs text-charcoal/60 space-y-1" data-testid="announce-delivery-status">
            <button type="button" onClick={() => setShowStatus((s) => !s)} data-testid="announce-status-toggle-btn"
              className="underline font-medium text-charcoal/80">
              Who has received it? {status.sent.length} sent · {status.unsent.length} not yet sent{status.skipped_no_email.length ? ` · ${status.skipped_no_email.length} without an email address` : ''} {showStatus ? '▲' : '▼'}
            </button>
            {showStatus && (
              <div className="space-y-2 p-3 rounded-xl bg-stone/60">
                {status.unsent.length > 0 && (
                  <div data-testid="announce-unsent-list"><span className="font-semibold text-heritage">Not yet sent ({status.unsent.length}):</span>{' '}
                    {status.unsent.map((s) => `${s.business} (${s.email})`).join('; ')}</div>
                )}
                {status.sent.length > 0 && (
                  <div data-testid="announce-sent-list"><span className="font-semibold text-forest">Sent ({status.sent.length}):</span>{' '}
                    {status.sent.map((s) => s.business).join('; ')}</div>
                )}
                {status.skipped_no_email.length > 0 && (
                  <div><span className="font-semibold">No email address ({status.skipped_no_email.length}):</span>{' '}
                    {status.skipped_no_email.join('; ')}</div>
                )}
              </div>
            )}
          </div>
        )}
        {msg && <div data-testid="announce-result" className={`p-3 rounded-xl text-sm ${msg.ok ? 'bg-forest/10 text-forest' : 'bg-heritage/10 text-heritage'}`}>{msg.text}</div>}
      </div>
    </div>
  );
}

function VideosPanel() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('');
  const [deadline, setDeadline] = useState('');
  const [closed, setClosed] = useState(false);
  const [err, setErr] = useState('');
  const [playing, setPlaying] = useState(null); // submission being watched
  const [requesting, setRequesting] = useState(null);
  const [allCats, setAllCats] = useState([]);
  const [catEdit, setCatEdit] = useState(null); // {id, business, category, extra[]}
  const [catSaving, setCatSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/admin/awards/videos${filter ? `?category=${encodeURIComponent(filter)}` : ''}`);
      setItems(r.data.items || []); setCategories(r.data.categories || []);
      setAllCats(r.data.all_categories || r.data.categories || []);
    } catch { setErr('Failed to load submissions'); }
  }
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get('/site-settings/awards_video_deadline').then((r) => {
      const v = r.data?.value || {};
      setDeadline(v.date || ''); setClosed(!!v.closed);
    }).catch(() => {});
  }, []);

  async function saveDeadline(nextClosed = closed) {
    await api.put('/admin/site-settings/awards_video_deadline', { value: { date: deadline || null, closed: nextClosed } });
  }

  async function download(v) {
    try {
      const r = await api.get(`/admin/awards/videos/${v.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${v.business_name} - ${v.filename}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert('Download failed — try again'); }
  }

  async function requestReupload(v) {
    if (!window.confirm(`Email ${v.contact_name || v.business_name} (${v.email}) asking them to re-upload their video?`)) return;
    setRequesting(v.id);
    try {
      const r = await api.post(`/admin/awards/videos/${v.id}/request-reupload`);
      alert(`Re-upload request sent to ${r.data.sent_to}`);
      load();
    } catch (ex) {
      alert(ex.response?.data?.detail || 'Email failed to send — try again');
    } finally { setRequesting(null); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-charcoal/10 rounded-2xl p-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="font-semibold">Submission deadline:</span>
        <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
          data-testid="video-deadline-input" className="border rounded-lg px-2.5 py-1.5"/>
        <button onClick={() => saveDeadline()} data-testid="video-deadline-save" className="px-3 py-1.5 rounded-lg bg-charcoal text-cream font-semibold">Save</button>
        <label className="flex items-center gap-2 ml-2">
          <input type="checkbox" checked={closed} onChange={(e) => { setClosed(e.target.checked); saveDeadline(e.target.checked); }} data-testid="video-close-toggle"/>
          Close submissions now
        </label>
        <span className="text-charcoal/50 text-xs">The upload page shows the deadline and closes automatically after it passes. Share the link: <code className="bg-stone px-1.5 py-0.5 rounded">/awards/finalist-video-upload</code></span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="video-category-filter" className="border rounded-xl px-3 py-2 bg-white">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={load} data-testid="videos-refresh-btn" className="px-4 py-2 rounded-xl border flex items-center gap-2"><RefreshCw size={14}/> Refresh</button>
        <button onClick={() => window.open(`${API_BASE}/admin/awards/videos/download-all?token=${encodeURIComponent(localStorage.getItem('gl_admin_token') || '')}`, '_blank')}
          disabled={items.length === 0} data-testid="videos-download-all-btn"
          className="px-4 py-2 rounded-xl bg-charcoal text-cream font-semibold flex items-center gap-2 disabled:opacity-50">
          <Download size={14}/> Download all (zip)
        </button>
        <button onClick={() => window.open(`${API_BASE}/admin/awards/finalist-images/download-all?token=${encodeURIComponent(localStorage.getItem('gl_admin_token') || '')}`, '_blank')}
          data-testid="finalist-images-download-btn"
          className="px-4 py-2 rounded-xl bg-heritage text-cream font-semibold flex items-center gap-2">
          <Download size={14}/> Finalist images for programme (zip)
        </button>
        <span className="text-sm text-charcoal/60">{items.length} submission{items.length === 1 ? '' : 's'}</span>
      </div>
      {err && <div className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
          <thead className="bg-admin text-left text-xs uppercase tracking-wide text-charcoal/60">
            <tr><th className="p-3">Business</th><th className="p-3">Category</th><th className="p-3">Contact</th><th className="p-3">Submitted</th><th className="p-3">Declaration</th><th className="p-3">Video</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} data-testid={`video-row-${v.id}`} className="border-t border-charcoal/5 text-sm">
                <td className="p-3 font-medium">{v.business_name}</td>
                <td className="p-3 text-charcoal/70">
                  <div>{v.category}</div>
                  {(v.extra_categories || []).map((c) => (
                    <div key={c} className="text-[11px] text-charcoal/50" data-testid={`video-extra-cat-${v.id}`}>also: {c}</div>
                  ))}
                  <button onClick={() => setCatEdit({ id: v.id, business: v.business_name, category: v.category, extra: v.extra_categories || [] })}
                    data-testid={`video-cat-edit-${v.id}`} className="text-[11px] text-heritage underline mt-0.5">
                    Change / add categories
                  </button>
                </td>
                <td className="p-3"><div>{v.contact_name}</div><div className="text-xs text-charcoal/50">{v.email}{v.phone ? ` · ${v.phone}` : ''}</div></td>
                <td className="p-3 whitespace-nowrap">{fmtDate(v.submitted_at)}</td>
                <td className="p-3">
                  <span data-testid={`video-declaration-${v.id}`} title={`Accepted ${fmtDate(v.declaration_accepted_at)}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-forest/10 text-forest"><CheckCircle2 size={10}/> Accepted</span>
                </td>
                <td className="p-3"><div className="text-xs">{v.filename}</div><div className="text-[11px] text-charcoal/50">{v.size ? `${(v.size / (1024 * 1024)).toFixed(1)}MB` : ''}</div>
                  {v.playback_status === 'processing' && <span data-testid={`video-converting-${v.id}`} className="inline-block mt-0.5 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-mustard/30 text-charcoal">Checking &amp; preparing…</span>}
                  {v.playback_status === 'failed' && <span data-testid={`video-convert-failed-${v.id}`} className="inline-block mt-0.5 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-heritage/15 text-heritage" title={v.playback_error || 'Use Download to watch it in QuickTime/VLC'}>Play may not work — use Download</span>}
                  {v.playback_status === 'ready' && (v.playback_note || '').startsWith('⚠') && <span data-testid={`video-partial-${v.id}`} className="inline-block mt-0.5 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-mustard text-charcoal" title={v.playback_note}>Plays — but file incomplete</span>}
                  {v.playback_status === 'corrupted' && <span data-testid={`video-corrupted-${v.id}`} className="inline-block mt-0.5 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-red-600 text-white" title={v.playback_error || 'The uploaded file is damaged or incomplete'}>File damaged — ask the business to re-upload</span>}
                  {(v.playback_status === 'corrupted' || (v.playback_status === 'ready' && (v.playback_note || '').startsWith('⚠'))) && (
                    v.reupload_requested_at ? (
                      <div className="mt-1">
                        <span data-testid={`reupload-requested-${v.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-forest"><CheckCircle2 size={10}/> Re-upload requested {fmtDate(v.reupload_requested_at)}</span>
                        <button onClick={() => requestReupload(v)} data-testid={`reupload-again-${v.id}`} className="ml-2 text-[10px] underline text-charcoal/50 hover:text-charcoal">send again</button>
                      </div>
                    ) : (
                      <button onClick={() => requestReupload(v)} data-testid={`request-reupload-${v.id}`} disabled={requesting === v.id}
                        className="block mt-1 text-[11px] font-semibold bg-heritage text-cream px-2.5 py-1 rounded-full hover:bg-heritage/80 disabled:opacity-50">
                        {requesting === v.id ? 'Sending…' : 'Email re-upload request'}
                      </button>
                    )
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setPlaying(v)} data-testid={`video-play-${v.id}`} title="Watch video"
                    className="p-1.5 rounded-lg hover:bg-stone text-charcoal"><Play size={14}/></button>
                  <button onClick={() => download(v)} data-testid={`video-download-${v.id}`} title="Download original file"
                    className="p-1.5 rounded-lg hover:bg-stone text-forest"><Download size={14}/></button>
                  <button onClick={async () => { if (window.confirm(`Delete ${v.business_name}'s video? The file is removed from storage.`)) { await api.delete(`/admin/awards/videos/${v.id}`); load(); } }}
                    data-testid={`video-delete-${v.id}`} className="p-1.5 rounded-lg hover:bg-stone text-heritage"><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-charcoal/50">No videos submitted yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {catEdit && (
        <div className="fixed inset-0 z-50 bg-charcoal/70 flex items-center justify-center p-4" onClick={() => setCatEdit(null)} data-testid="video-cat-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-1">Video categories — {catEdit.business}</h3>
            <p className="text-xs text-charcoal/60 mb-4">Change the main category to move a video uploaded to the wrong one. Tick additional categories where this venue is also a finalist — the video then appears under those too.</p>
            <label className="text-xs uppercase tracking-wide text-charcoal/50 font-bold block mb-1">Main category</label>
            <select value={catEdit.category} data-testid="video-cat-primary"
              onChange={(e) => setCatEdit({ ...catEdit, category: e.target.value, extra: catEdit.extra.filter((x) => x !== e.target.value) })}
              className="border rounded-xl px-3 py-2 w-full mb-4 bg-white text-sm">
              {[...new Set([catEdit.category, ...allCats])].filter(Boolean).map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="text-xs uppercase tracking-wide text-charcoal/50 font-bold mb-1">Also show in</div>
            <div className="space-y-1.5 mb-5 max-h-56 overflow-y-auto border border-charcoal/10 rounded-xl p-3">
              {allCats.filter((c) => c !== catEdit.category).map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={catEdit.extra.includes(c)}
                    data-testid={`video-cat-extra-${c.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                    onChange={(e) => setCatEdit({ ...catEdit, extra: e.target.checked ? [...catEdit.extra, c] : catEdit.extra.filter((x) => x !== c) })} />
                  {c}
                </label>
              ))}
              {allCats.filter((c) => c !== catEdit.category).length === 0 && <div className="text-xs text-charcoal/50">No other categories available.</div>}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCatEdit(null)} data-testid="video-cat-cancel" className="px-4 py-2 rounded-full border text-sm">Cancel</button>
              <button data-testid="video-cat-save" disabled={catSaving}
                onClick={async () => {
                  setCatSaving(true);
                  try {
                    await api.patch(`/admin/awards/videos/${catEdit.id}/categories`, { category: catEdit.category, extra_categories: catEdit.extra });
                    setCatEdit(null); load();
                  } catch { setErr('Could not save categories'); }
                  setCatSaving(false);
                }}
                className="px-5 py-2 rounded-full bg-forest text-base text-sm font-semibold disabled:opacity-50">
                {catSaving ? 'Saving…' : 'Save categories'}
              </button>
            </div>
          </div>
        </div>
      )}
      {playing && (
        <div className="fixed inset-0 z-50 bg-charcoal/80 flex items-center justify-center p-4" onClick={() => setPlaying(null)} data-testid="video-player-modal">
          <div className="bg-charcoal rounded-2xl overflow-hidden max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 text-cream text-sm">
              <span className="truncate">{playing.business_name} — {playing.category}</span>
              <button onClick={() => setPlaying(null)} data-testid="video-player-close" className="px-3 py-1 rounded-lg bg-cream/10 hover:bg-cream/20">Close</button>
            </div>
            <video key={playing.id} controls autoPlay playsInline className="w-full max-h-[70vh] bg-black" data-testid="video-player"
              src={`${API_BASE}/admin/awards/videos/${playing.id}/stream?token=${encodeURIComponent(localStorage.getItem('gl_admin_token') || '')}`}/>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminFinalists() {
  const [tab, setTab] = useState('finalists');
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Trophy className="text-heritage" size={28}/>
        <h1 className="font-display text-3xl">2026 Finalists</h1>
      </div>
      <div className="flex gap-2 border-b border-charcoal/10">
        <button data-testid="finalists-tab-list" onClick={() => setTab('finalists')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'finalists' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><Trophy size={16}/> Finalist list</span>
        </button>
        <button data-testid="finalists-tab-videos" onClick={() => setTab('videos')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'videos' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><Clapperboard size={16}/> Ceremony videos</span>
        </button>
        <button data-testid="finalists-tab-campaigns" onClick={() => setTab('campaigns')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'campaigns' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          Email finalists
        </button>
        <button data-testid="finalists-tab-announce" onClick={() => setTab('announce')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'announce' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><Send size={16}/> Announcement email</span>
        </button>
      </div>
      {tab === 'finalists' ? <FinalistsPanel/> : tab === 'videos' ? <VideosPanel/> : tab === 'campaigns' ? <FinalistCampaigns/> : <AnnouncementPanel/>}
    </div>
  );
}
